import map from "lodash/map"
import isEmpty from "lodash/isEmpty"
import get from "lodash/get"
import filter from "lodash/filter"
import { Component } from "react"
import PropTypes from "prop-types"
import { withLocalization } from "hoc-lib/localization"
import { logRocketTrack } from "utils-lib/logRocket"
import { withLDConsumer } from "launchdarkly-react-client-sdk"
import { CustomRadioButton, ButtonPrimary, GenericModal } from "../../../Base"
import "./AddressConfirmation.scss"

export const strings = {
  en_CA: {
    TITLE_FOR_ADDRESS_CONFIRMATION_MODAL: `Please choose a valid address that matches the one you entered.`,
    LABEL_FOR_USER_TYPED_ADDRESS: `The address you entered was:`,
    CHOOSE_ADDRESS: `Choose one of the addresses we found:`,
    CHOOSE_MATCHED_ADDRESS: `Please select one of the addresses we found.`,
    SUBMIT_BUTTON_LABEL: `USE SELECTED ADDRESS`,
  },
  fr_CA: {
    TITLE_FOR_ADDRESS_CONFIRMATION_MODAL: `Veuillez choisir une adresse valide qui correspond à celle que vous avez saisie.`,
    LABEL_FOR_USER_TYPED_ADDRESS: `L'adresse que vous avez saisie était:`,
    CHOOSE_ADDRESS: `Choisissez une des adresses trouvées:`,
    CHOOSE_MATCHED_ADDRESS: `Veuillez sélectionner une des adresses que nous avons trouvées.`,
    SUBMIT_BUTTON_LABEL: `Utiliser l'adresse sélectionnée`,
  },
}

export class AddressConfirmation extends Component {
  constructor(props) {
    super(props)

    this.state = {
      selectedSuggestion: {},
      errorMessage: ``,
      handleOnInput: false, // Logrocket track input stroke for once
      localFlags: {
        LDFFReleasePreviousAddressGettingPassedInGetMeStartedApi:
          this.props.ldClient?.variation(
            "LDFFReleasePreviousAddressGettingPassedInGetMeStartedApi",
            false,
          ) || false,

        LDFFReleaseCheckAvailabilityButtonStaysDisabled:
          this.props.ldClient?.variation(
            "LDFFReleaseCheckAvailabilityButtonStaysDisabled",
            false,
          ) || false,
      },
    }
  }

  onClose = () => {
    this.setState({
      selectedSuggestion: {},
    })
    this.props.onClose()
  }

  componentDidUpdate(prevProps) {
    if (this.props.open !== prevProps.open && this.props.open) {
      if (!this.state.handleOnInput) {
        logRocketTrack(`madlib-modal-false`)
        this.setState({
          handleOnInput: false,
        })
      }
    }
  }

  render() {
    const { strings, showLocationIcon } = this.props

    return (
      <div>
        <GenericModal
          open={this.props.open}
          onClose={this.onClose}
          showCloseButton
          useFlexibleWidth
          customDialogueClass="p-0"
          scroll={showLocationIcon ? `body` : `paper`}
        >
          <div className="AddressConfirmation px-3 px-sm-5 py-5">
            <div
              className="conf-font-large maax-font-weight-regular pb-4 pb-sm-5"
              data-testid="AddressConfirmation"
            >
              {strings.TITLE_FOR_ADDRESS_CONFIRMATION_MODAL}
            </div>
            <div className="maax-font-weight-bold wm-text-gray-dark pb-3">
              {strings.LABEL_FOR_USER_TYPED_ADDRESS}
            </div>
            <div className="conf-font-large pb-4 pb-sm-5 maax-font-weight-regular">
              {this.props.userInput}
            </div>
            <div className="maax-font-weight-bold wm-text-gray-dark pb-3">
              {strings.CHOOSE_ADDRESS}
            </div>
            <div className="pb-4 pb-sm-5">
              {map(this.props.suggestions, (suggestion) => {
                const addressLabel = get(suggestion, `label`, ``)
                if (
                  this.props.suggestions.length === 1 &&
                  isEmpty(this.state.selectedSuggestion)
                ) {
                  this.setState({
                    selectedSuggestion: suggestion,
                    errorMessage: ``,
                  })
                  this.props.onConfirm(this.state.selectedSuggestion)
                }

                return (
                  <CustomRadioButton
                    testid="AddressConfirmation - selection"
                    key={addressLabel}
                    className="pb-3"
                    size="lg"
                    label={addressLabel}
                    onClick={() => {
                      this.setState({
                        selectedSuggestion: suggestion,
                        errorMessage: ``,
                      })

                      const {
                        LDFFReleasePreviousAddressGettingPassedInGetMeStartedApi,
                      } = this.state.localFlags

                      if (
                        LDFFReleasePreviousAddressGettingPassedInGetMeStartedApi
                      ) {
                        this.props.onSelect(suggestion)
                      } else {
                        this.props.onSelect(addressLabel)
                      }
                    }}
                    isChecked={
                      addressLabel ===
                        get(this.state.selectedSuggestion, `label`, ``) ||
                      this.props.suggestions.length === 1
                    }
                  />
                )
              })}
              {this.state.errorMessage !== `` ? (
                <div className="error-message wm-text-danger maax-font-weight-regular">
                  {this.state.errorMessage}
                </div>
              ) : null}
            </div>
            <div className="d-flex justify-content-center w-100">
              <ButtonPrimary
                testId="AddressConfirmation - confirm button"
                analytics_key={`Geosuggest ${strings.SUBMIT_BUTTON_LABEL}`}
                padding="px-5"
                disabled={
                  isEmpty(this.state.selectedSuggestion) ||
                  !filter(
                    this.props.suggestions,
                    (suggestion) =>
                      this.state.selectedSuggestion.placeId ===
                      suggestion.placeId,
                  ).length
                }
                onClick={() => {
                  if (isEmpty(this.state.selectedSuggestion)) {
                    this.setState({
                      errorMessage: strings.CHOOSE_MATCHED_ADDRESS,
                    })
                    return
                  }
                  const { LDFFReleaseCheckAvailabilityButtonStaysDisabled } =
                    this.state.localFlags

                  if (LDFFReleaseCheckAvailabilityButtonStaysDisabled) {
                    this.props.setCustomerAddressFromGeocode(
                      this.state.selectedSuggestion.label,
                    )
                  }
                  // Select first google suggested address.
                  this.props.onConfirm(this.state.selectedSuggestion)

                  this.onClose()
                }}
              >
                {strings.SUBMIT_BUTTON_LABEL}
              </ButtonPrimary>
            </div>
          </div>
        </GenericModal>
      </div>
    )
  }
}

AddressConfirmation.propTypes = {
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  open: PropTypes.bool,
  suggestions: PropTypes.array,
  userInput: PropTypes.string,
  strings: PropTypes.object.isRequired,
  showLocationIcon: PropTypes.bool,
}

AddressConfirmation.defaultProps = {
  open: false,
  suggestions: [],
  userInput: ``,
  showLocationIcon: true,
}

const LdfContainerAddressConfirmation = withLDConsumer()(AddressConfirmation)

export default withLocalization(strings)(LdfContainerAddressConfirmation)
